import { create } from 'zustand';
import api from '../services/api';
import type { LivingType, Settings } from '../types';

interface SettingsState {
  settings: Settings | null;
  isLoading: boolean;

  // Actions
  fetchSettings: () => Promise<void>;
  setSettings: (settings: Settings) => void;
  setLivingType: (livingType: LivingType) => void;
  setLoading: (loading: boolean) => void;
  clearSettings: () => void;
}

const DEFAULT_SETTINGS: Settings = {
  livingType: 'couple',
  partner1Percentage: 50,
  partner2Percentage: 50,
  calculationMode: 'proportional',
  accountType: 'joint_with_personal',
  jointAccountStartDate: null,
  jointAccountStartingBalance: null,
  personalBudgetStartDate: null,
  personalBudgetStartingBalance: null,
  customReferencePartner: 'partner1',
  partner2ManualMonthlyIncome: null,
};

export const useSettingsStore = create<SettingsState>()((set) => ({
  settings: null,
  isLoading: false,

  fetchSettings: async () => {
    try {
      set({ isLoading: true });
      const response = await api.get('/settings');
      if (response.data.success && response.data.data) {
        set({ settings: response.data.data });
      }
    } catch {
      // Ignorer les erreurs - les settings par défaut restent valides
    } finally {
      set({ isLoading: false });
    }
  },

  setSettings: (settings) => set({ settings }),

  setLivingType: (livingType) =>
    set((state) => ({
      settings: state.settings
        ? { ...state.settings, livingType }
        : { ...DEFAULT_SETTINGS, livingType },
    })),

  setLoading: (loading) => set({ isLoading: loading }),

  clearSettings: () => set({ settings: null }),
}));

// Selector pour obtenir le type de vie avec fallback
export const useLivingType = (): LivingType => {
  const settings = useSettingsStore((state) => state.settings);
  return settings?.livingType ?? 'couple';
};
